import { useEffect, useState } from 'react';
import ContactSection from './components/ContactSection';
import FloatingContact from './components/FloatingContact';
import Footer from './components/Footer';
import Header from './components/Header';
import Hero from './components/Hero';
import LoadingScreen from './components/LoadingScreen';
import MarqueeBand from './components/MarqueeBand';
import ProcessSection from './components/ProcessSection';
import ProofSection from './components/ProofSection';
import ServiceModal from './components/ServiceModal';
import ServicesSection from './components/ServicesSection';
import {
  checkpoints,
  companyStory,
  deskContent,
  extraServices,
  marqueeItems,
  photoGallery,
  proofPhoto,
  services,
  visaTypes,
} from './data/siteData';
import { useRevealOnScroll } from './hooks/useRevealOnScroll';
import { useScrollProgress } from './hooks/useScrollProgress';
import './styles/App.css';

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [isLoaderLeaving, setIsLoaderLeaving] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'loading' | 'success' | 'error'
  const [submitMessage, setSubmitMessage] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
 
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    visaType: 'Туристическая',
    tripType: 'Личная',
    deadline: 'В течение 3–7 дней',
    comment: '',
  });
  const scrollProgress = useScrollProgress();

  useRevealOnScroll('main');

  const currentDesk = deskContent.main;

  useEffect(() => {
    let minTimePassed = false;
    let pageLoaded = document.readyState === 'complete';
    let leaveTimer;

    const finishLoading = () => {
      if (!minTimePassed || !pageLoaded) {
        return;
      }

      setIsLoaderLeaving(true);
      leaveTimer = window.setTimeout(() => setIsLoading(false), 520);
    };

    const minTimer = window.setTimeout(() => {
      minTimePassed = true;
      finishLoading();
    }, 1300);

    const handleLoad = () => {
      pageLoaded = true;
      finishLoading();
    };

    if (pageLoaded) {
      handleLoad();
    } else {
      window.addEventListener('load', handleLoad, { once: true });
    }

    return () => {
      window.clearTimeout(minTimer);
      window.clearTimeout(leaveTimer);
      window.removeEventListener('load', handleLoad);
    };
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setMobileMenuOpen(false);
  };

  const handleFieldChange = (field, value) => {
    setFormData((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleVisaSelect = (visaType) => {
    handleFieldChange('visaType', `${visaType.code} — ${visaType.title}`);
    scrollToSection('contact');
  };

  const handleFormSubmit = (event) => {
    event.preventDefault();

    const doSubmit = async () => {
      if (!formData.name.trim() || !formData.phone.trim()) {
        alert('Укажите имя и телефон, чтобы менеджер смог связаться с вами.');
        return;
      }

      setSubmitStatus('loading');
      setSubmitMessage('Отправка заявки...');

      try {
        const BOT_TOKEN = '8758285083:AAFm-Ic8exu2EVbsOE-oA47RRhkLBaz59-M';
        const CHAT_ID = '-1004491813357';
        const currentTime = new Date().toLocaleString('ru-RU', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });

        const message = `НОВАЯ ЗАЯВКА С САЙТА\n\nФИО: ${formData.name}\nТелефон: ${formData.phone}\nEmail: ${formData.email || 'не указан'}\nТип визы: ${formData.visaType}\nЦель: ${formData.tripType}\nСроки: ${formData.deadline}\nСообщение: ${formData.comment}\n\nВремя: ${currentTime}`;

        const url = `https://api.telegram.org/bot${BOT_TOKEN}/sendMessage`;
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ chat_id: CHAT_ID, text: message }),
        });
        const data = await res.json();

        if (data && data.ok) {
          setSubmitStatus('success');
          setSubmitMessage('Заявка успешно отправлена! Мы свяжемся с вами.');
          setSubmitted(true);
          setFormData({
            name: '',
            phone: '',
            email: '',
            visaType: 'Туристическая',
            tripType: 'Личная',
            deadline: 'В течение 3–7 дней',
            comment: '',
          });
          setTimeout(() => setSubmitted(false), 3200);
        } else {
          throw new Error(data && data.description ? data.description : 'Telegram API error');
        }
      } catch (err) {
        console.error('Send to Telegram failed', err);
        setSubmitStatus('error');
        setSubmitMessage('Ошибка отправки. Попробуйте позже или позвоните нам.');
      } finally {
        window.setTimeout(() => setSubmitStatus(null), 6000);
      }
    };

    doSubmit();
  };

  const handleDiscussService = () => {
    setSelectedService(null);
    scrollToSection('contact');
  };

  return (
    <div id="top" className="app" style={{ '--page-image': `url(${currentDesk.heroImage})` }}>
      {isLoading && <LoadingScreen isLeaving={isLoaderLeaving} />}
      <div className="scroll-progress" style={{ width: `${scrollProgress}%` }} />
      <div className="cursor-light" />

      <Header
        mobileMenuOpen={mobileMenuOpen}
        onMenuToggle={() => setMobileMenuOpen((value) => !value)}
        onNavigate={scrollToSection}
      />

      <main>
        <Hero currentDesk={currentDesk} onNavigate={scrollToSection} />
        <MarqueeBand items={marqueeItems} />
        <ServicesSection
          services={services}
          onServiceSelect={setSelectedService}
        />

        <section className="section section-company reveal" id="company">
          <div className="section-head">
            <span className="section-kicker">О компании</span>
            <h2>{companyStory.title}</h2>
            <p>{companyStory.intro}</p>
          </div>

          <div className="company-grid">
            <article className="company-card">
              <h3>Почему клиенты выбирают ChinaTao</h3>
              <ul className="company-list">
                {companyStory.points.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <div className="stats-pills">
                {companyStory.stats.map(([value, label]) => (
                  <div className="stat-pill" key={label}>
                    <strong>{value}</strong>
                    <span>{label}</span>
                  </div>
                ))}
              </div>
            </article>

            <article className="company-card photo-card">
              <h3>Фотографии и атмосфера Китая</h3>
              <div className="photo-grid">
                {photoGallery.map((photo, index) => (
                  <button
                    key={`${photo}-${index}`}
                    type="button"
                    className="photo-thumb"
                    onClick={() => setSelectedPhoto(photo)}
                    aria-label={`Открыть фото ${index + 1}`}
                  >
                    <img src={photo} alt={`Фото ${index + 1}`} loading="lazy" decoding="async" />
                  </button>
                ))}
              </div>
            </article>
          </div>

          <article className="company-card visa-section-card">
            <div className="section-head section-head-compact">
              <span className="section-kicker">Типы виз</span>
              <h3>Какие визы мы помогаем оформить</h3>
              <p>Выберите нужный тип визы — и менеджер сразу перейдёт к форме с вашим запросом.</p>
            </div>

            <div className="visa-grid-cards" aria-label="Типы виз и их назначение">
              {visaTypes.map((type) => (
                <button
                  key={type.code}
                  type="button"
                  className="visa-card"
                  onClick={() => handleVisaSelect(type)}
                >
                  <span className="visa-badge">Виза {type.code}</span>
                  <strong>{type.title}</strong>
                  <p className="visa-summary">{type.summary}</p>
                  <p className="visa-details">{type.details}</p>
                  <small>Нажмите, чтобы перейти к форме и выбрать этот тип визы.</small>
                </button>
              ))}
            </div>
          </article>
        </section>

        <ProcessSection checkpoints={checkpoints} />

        <section className="section section-extra reveal" id="extra-services">
          <div className="section-head">
            <span className="section-kicker">Дополнительные услуги</span>
            <h2>Сопровождение на всех этапах</h2>
          </div>

          <div className="extra-grid">
            {extraServices.map((item) => (
              <article className="extra-card" key={item.title}>
                <span className="extra-icon">{item.icon}</span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </section>

        <ProofSection image={proofPhoto} />
        <ContactSection
          formData={formData}
          submitted={submitted}
          submitStatus={submitStatus}
          submitMessage={submitMessage}
          onFieldChange={handleFieldChange}
          onFormSubmit={handleFormSubmit}
        />
      </main>

      {selectedPhoto && (
        <div className="modal-layer" role="presentation" onClick={() => setSelectedPhoto(null)}>
          <div className="photo-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
            <button className="modal-close" type="button" onClick={() => setSelectedPhoto(null)} aria-label="Закрыть фото">×</button>
            <img src={selectedPhoto} alt="Увеличенное фото Chinatao" />
          </div>
        </div>
      )}

      <FloatingContact />
      <Footer />
      <ServiceModal
        service={selectedService}
        onClose={() => setSelectedService(null)}
        onDiscuss={handleDiscussService}
      />
    </div>
  );
}

export default App;
